/**
 * EditorWorkspace — Main container for the AI Editor Pipeline
 *
 * Three-column layout: editor grid | document | results
 * Non-linear: all 7 editors always available, no disabled states.
 */

import { useState, useCallback, useEffect } from 'react'
import {
  Save,
  Download,
  ChevronDown,
  ArrowLeft,
  History,
  FileText,
  Trash2,
  Loader2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'
import type {
  EditorSessionDetail,
  EditorSessionSummary,
  EditorTypeKey,
} from '@/lib/api'
import { EDITOR_PERSONAS } from '@/lib/api'
import { DocumentInput } from './DocumentInput'
import { EditorCard } from './EditorCard'
import { EditorResultPanel } from './EditorResultPanel'
import { VersionTimeline } from './VersionTimeline'
import { DocumentViewer } from './DocumentViewer'

type View = 'list' | 'input' | 'workspace'

export function EditorWorkspace() {
  const [view, setView] = useState<View>('list')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [session, setSession] = useState<EditorSessionDetail | null>(null)
  const [sessions, setSessions] = useState<EditorSessionSummary[]>([])
  const [, setLoading] = useState(false)
  const [runningEditor, setRunningEditor] = useState<EditorTypeKey | null>(null)
  const [activeResult, setActiveResult] = useState<EditorTypeKey | null>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showVersions, setShowVersions] = useState(false)

  // Load session list
  useEffect(() => {
    api.editor.sessions.list().then(r => setSessions(r.sessions)).catch(() => {})
  }, [])

  // Load session detail when sessionId changes
  useEffect(() => {
    if (!sessionId) return
    setLoading(true)
    api.editor.sessions.get(sessionId)
      .then(s => {
        setSession(s)
        setView('workspace')
      })
      .catch(() => {
        notify.error('Failed to load session')
        setView('list')
      })
      .finally(() => setLoading(false))
  }, [sessionId])

  const refreshSession = useCallback(async () => {
    if (!sessionId) return
    const s = await api.editor.sessions.get(sessionId)
    setSession(s)
  }, [sessionId])

  // Session created from DocumentInput
  const handleSessionCreated = (id: string) => {
    setSessionId(id)
  }

  // Run an editor
  const handleRunEditor = useCallback(async (editorType: EditorTypeKey) => {
    if (!sessionId || runningEditor) return
    setRunningEditor(editorType)
    try {
      const promise = api.editor.run(sessionId, editorType)
      notify.aiGeneration(promise, {
        loading: `Running ${EDITOR_PERSONAS.find(p => p.key === editorType)?.displayName}...`,
        success: 'Editor pass complete!',
        error: 'Editor run failed',
      })
      await promise
      await refreshSession()
      setActiveResult(editorType)
    } catch {
      // notify already handled above
    } finally {
      setRunningEditor(null)
    }
  }, [sessionId, runningEditor, refreshSession])

  // Apply selected suggestions — stay on panel to show accepted state
  const handleApplySelected = useCallback(async (editorType: EditorTypeKey, indices: number[]) => {
    if (!sessionId) return
    await api.editor.apply(sessionId, editorType, indices)
    await refreshSession()
    notify.success('Suggestions applied')
  }, [sessionId, refreshSession])

  // Adopt full edit — stay on panel to show accepted state
  const handleAdoptAll = useCallback(async (editorType: EditorTypeKey) => {
    if (!sessionId) return
    await api.editor.adopt(sessionId, editorType)
    await refreshSession()
    notify.success('All changes adopted')
  }, [sessionId, refreshSession])

  // Rollback
  const handleRollback = useCallback(async (versionIndex: number) => {
    if (!sessionId) return
    await api.editor.rollback(sessionId, versionIndex)
    await refreshSession()
    notify.success(`Rolled back to version ${versionIndex}`)
  }, [sessionId, refreshSession])

  // Save snapshot
  const handleSnapshot = useCallback(async () => {
    if (!sessionId) return
    await api.editor.snapshot(sessionId, `Manual snapshot`)
    await refreshSession()
    notify.success('Snapshot saved')
  }, [sessionId, refreshSession])

  // Export
  const handleExport = useCallback(async (format: 'md' | 'docx' | 'pdf' | 'epub') => {
    if (!sessionId) return
    setShowExportMenu(false)
    try {
      await api.editor.export(sessionId, format)
      notify.success(`Exported as ${format.toUpperCase()}`)
    } catch (err) {
      notify.error('Export failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [sessionId])

  // Delete session
  const handleDelete = useCallback(async (id: string) => {
    await api.editor.sessions.delete(id)
    setSessions(prev => prev.filter(s => s.id !== id))
    if (sessionId === id) {
      setSessionId(null)
      setSession(null)
      setView('list')
    }
    notify.success('Session deleted')
  }, [sessionId])

  // ── List View ──────────────────────────────────────────────

  if (view === 'list') {
    return (
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex items-center justify-between bg-white/80 dark:bg-surface-900/80">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Editor Sessions</h2>
          <button
            onClick={() => setView('input')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            New Session
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {sessions.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No editor sessions yet.</p>
              <p className="text-xs mt-1">Create a new session to start editing with AI.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map(s => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 bg-white dark:bg-surface-800 cursor-pointer transition-colors"
                  onClick={() => setSessionId(s.id)}
                >
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {s.documentTitle || 'Untitled'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {s.documentWordCount?.toLocaleString() || '?'} words &middot; {s.editorsRun.length} editor{s.editorsRun.length !== 1 ? 's' : ''} run &middot; {s.qualityTier}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(s.id) }}
                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Input View ─────────────────────────────────────────────

  if (view === 'input') {
    return (
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-surface-900/80">
          <button
            onClick={() => setView('list')}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to Sessions
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <DocumentInput onSessionCreated={handleSessionCreated} />
        </div>
      </div>
    )
  }

  // ── Workspace View ─────────────────────────────────────────

  if (!session) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  const currentText = session.currentText || session.documentText

  return (
    <div className="h-full flex flex-col">
      {/* Top action bar */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-surface-900/80 flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => { setView('list'); setSessionId(null); setSession(null); setActiveResult(null) }}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {session.documentTitle || 'Untitled'}
          </h3>
          <div className="text-[10px] text-gray-400">
            {session.documentWordCount?.toLocaleString()} words &middot; {session.qualityTier} tier &middot; v{session.versionHistory.length - 1}
          </div>
        </div>

        {/* Action buttons */}
        <button
          onClick={handleSnapshot}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/15 rounded-md hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
        >
          <Save className="w-3 h-3" />
          Snapshot
        </button>

        <button
          onClick={() => setShowVersions(!showVersions)}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-md transition-colors ${
            showVersions
              ? 'text-indigo-600 border-indigo-300 bg-indigo-50 dark:text-indigo-400 dark:border-indigo-500/50 dark:bg-indigo-900/20'
              : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5'
          }`}
        >
          <History className="w-3 h-3" />
          History ({session.versionHistory.length})
        </button>

        {/* Export dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/15 rounded-md hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            <Download className="w-3 h-3" />
            Export
            <ChevronDown className="w-3 h-3" />
          </button>
          {showExportMenu && (
            <div className="absolute right-0 top-full mt-1 z-10 bg-white dark:bg-surface-800 border border-gray-200 dark:border-white/15 rounded-lg shadow-lg py-1 min-w-[120px]">
              {(['md', 'docx', 'pdf', 'epub'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => handleExport(f)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 flex">
        {/* Left column: Editor grid + version history */}
        <div className="w-72 border-r border-gray-200 dark:border-white/10 flex flex-col bg-gray-50 dark:bg-surface-900/50 flex-shrink-0 overflow-y-auto">
          {/* Version timeline (collapsible) */}
          {showVersions && (
            <div className="p-3 border-b border-gray-200 dark:border-white/10">
              <VersionTimeline
                versions={session.versionHistory}
                onRollback={handleRollback}
                currentVersion={session.versionHistory.length - 1}
              />
            </div>
          )}

          {/* Editor grid */}
          <div className="p-3 space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1">
              Editors
            </h4>
            {EDITOR_PERSONAS.map(persona => (
              <EditorCard
                key={persona.key}
                editorKey={persona.key}
                displayName={persona.displayName}
                emoji={persona.emoji}
                description={persona.description}
                suggestedOrder={persona.suggestedOrder}
                result={session.editorResults[persona.key] || null}
                isRunning={runningEditor === persona.key}
                onRun={() => handleRunEditor(persona.key)}
                onViewResult={() => setActiveResult(persona.key)}
              />
            ))}
          </div>
        </div>

        {/* Center/Right: Document or Result panel */}
        <div className="flex-1 min-w-0 flex flex-col">
          {activeResult && session.editorResults[activeResult] ? (
            <EditorResultPanel
              editorType={activeResult}
              result={session.editorResults[activeResult]}
              onApplySelected={(indices) => handleApplySelected(activeResult, indices)}
              onAdoptAll={() => handleAdoptAll(activeResult)}
              onClose={() => setActiveResult(null)}
              onRerun={() => handleRunEditor(activeResult)}
            />
          ) : (
            /* Document view */
            <DocumentViewer
              title={session.documentTitle || 'Untitled'}
              text={currentText}
              wordCount={session.documentWordCount ?? undefined}
              versionLabel={`v${session.versionHistory.length - 1}`}
            />
          )}
        </div>
      </div>
    </div>
  )
}
